import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle, Trash2, Eye, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const IncidentModal = () => {
  const [incidentForm, setIncidentForm] = useState({
    title: '',
    message: '',
    priority: 'normal',
    send_email_notification: false,
    estimated_duration: ''
  });
  const [incidentSuccess, setIncidentSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailsSent, setEmailsSent] = useState(0);
  const [incidents, setIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(true);

  const fetchIncidents = useCallback(async () => {
    try {
      const response = await api.get('/admin/incident-modals');
      setIncidents(response.data || []);
    } catch (error) {
      console.error('Error fetching incidents:', error);
    } finally {
      setLoadingIncidents(false);
    }
  }, []);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/admin/incident-modal', {
        title: incidentForm.title,
        message: incidentForm.message,
        priority: incidentForm.priority,
        send_email_notification: incidentForm.send_email_notification,
        estimated_duration: incidentForm.estimated_duration
      });
      setIncidentSuccess(true);
      setEmailsSent(response.data.emails_sent || 0);
      setIncidentForm({ title: '', message: '', priority: 'normal', send_email_notification: false, estimated_duration: '' });
      fetchIncidents();
      setTimeout(() => {
        setIncidentSuccess(false);
        setEmailsSent(0);
      }, 5000);
    } catch (error) {
      console.error('Error creating incident:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (modalId) => {
    try {
      await api.put(`/admin/incident-modal/${modalId}/deactivate`);
      fetchIncidents();
    } catch (error) {
      console.error('Error deactivating:', error);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return <XCircle className="h-5 w-5 text-[#FF3B5C]" />;
      case 'high': return <AlertTriangle className="h-5 w-5 text-[#FFB800]" />;
      default: return <Info className="h-5 w-5 text-[#00FFFF]" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-[#FF3B5C] bg-[#FF3B5C]/10';
      case 'high': return 'border-[#FFB800] bg-[#FFB800]/10';
      default: return 'border-[#00FFFF] bg-[#00FFFF]/10';
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  return (
    <div className="min-h-screen py-12 px-4" data-testid="incident-modal-page">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2">Modal Incident</h1>
          <p className="text-[#B8B8BA]">Cr&eacute;ez des alertes affich&eacute;es &agrave; tous les utilisateurs</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Form */}
          <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <AlertTriangle className="h-6 w-6 text-[#FFB800]" />
              <span>Cr&eacute;er une alerte</span>
            </h2>
            
            {incidentSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 mb-6 flex items-center space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-[#00FF88]" />
                <div>
                  <p className="text-[#00FF88]">Modale d&apos;incident cr&eacute;&eacute;e avec succ&egrave;s !</p>
                  {emailsSent > 0 && (
                    <p className="text-[#00FF88]/80 text-sm">{emailsSent} email(s) envoy&eacute;(s) aux clients</p>
                  )}
                </div>
              </motion.div>
            )}

            <form onSubmit={handleCreateIncident} className="space-y-6">
              <div>
                <label className="block text-[#E8E8E8] font-semibold mb-3">Priorit&eacute;</label>
                <div className="grid grid-cols-3 gap-3">
                  {['normal', 'high', 'critical'].map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => setIncidentForm({ ...incidentForm, priority })}
                      className={`p-3 rounded-lg border transition-all flex flex-col items-center space-y-1 ${
                        incidentForm.priority === priority
                          ? getPriorityColor(priority)
                          : 'border-[#3A3A3D] bg-[#222226] hover:border-[#6B6B6E]'
                      }`}
                      data-testid={`priority-${priority}`}
                    >
                      {getPriorityIcon(priority)}
                      <span className="text-white text-sm capitalize">
                        {priority === 'normal' ? 'Normale' : priority === 'high' ? '\u00c9lev\u00e9e' : 'Critique'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[#E8E8E8] font-semibold mb-3">Titre</label>
                <input
                  type="text"
                  value={incidentForm.title}
                  onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  placeholder="Maintenance programm\u00e9e"
                  required
                  data-testid="incident-title-input"
                />
              </div>

              <div>
                <label className="block text-[#E8E8E8] font-semibold mb-3">Message</label>
                <textarea
                  value={incidentForm.message}
                  onChange={(e) => setIncidentForm({ ...incidentForm, message: e.target.value })}
                  rows="4"
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  placeholder="Nos serveurs seront en maintenance le..."
                  required
                  data-testid="incident-message-input"
                />
              </div>

              <div>
                <label className="block text-[#E8E8E8] font-semibold mb-3">Dur&eacute;e estim&eacute;e (optionnel)</label>
                <input
                  type="text"
                  value={incidentForm.estimated_duration}
                  onChange={(e) => setIncidentForm({ ...incidentForm, estimated_duration: e.target.value })}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  placeholder="Ex: 2 heures, 30 minutes..."
                  data-testid="incident-duration-input"
                />
              </div>

              <div className="bg-[#222226] p-4 rounded-lg border border-[#FFB800]/30">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={incidentForm.send_email_notification}
                    onChange={(e) => setIncidentForm({ ...incidentForm, send_email_notification: e.target.checked })}
                    className="w-5 h-5 accent-[#FFB800] rounded"
                    data-testid="incident-email-checkbox"
                  />
                  <div>
                    <span className="text-white font-semibold">Envoyer un email &agrave; tous les clients</span>
                    <p className="text-[#B8B8BA] text-sm">Notifier automatiquement tous les clients actifs de cet incident</p>
                  </div>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary cyber-glow-hover disabled:opacity-50"
                data-testid="create-incident-btn"
              >
                {loading ? 'Cr\u00e9ation...' : "Cr\u00e9er l'alerte"}
              </button>
            </form>
          </div>

          {/* Preview & Info */}
          <div className="space-y-6">
            <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                <Eye className="h-5 w-5 text-[#00FFFF]" />
                <span>Aper&ccedil;u en direct</span>
              </h3>
              {incidentForm.title || incidentForm.message ? (
                <div className={`p-4 rounded-lg border ${getPriorityColor(incidentForm.priority)}`}>
                  <div className="flex items-start space-x-3">
                    {getPriorityIcon(incidentForm.priority)}
                    <div>
                      <h4 className="text-white font-bold">{incidentForm.title || 'Titre...'}</h4>
                      <p className="text-[#B8B8BA] text-sm mt-1">{incidentForm.message || 'Message...'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-[#6B6B6E] text-center py-8">
                  Remplissez le formulaire pour voir l&apos;aper&ccedil;u
                </div>
              )}
            </div>

            <div className="bg-[#222226] p-6 rounded-lg">
              <h3 className="text-white font-bold mb-4 flex items-center space-x-2">
                <Info className="h-5 w-5 text-[#00FFFF]" />
                <span>&Agrave; propos de la modale incident</span>
              </h3>
              <ul className="space-y-3 text-[#B8B8BA] text-sm">
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">*</span>
                  <span>La modale s&apos;affiche automatiquement sur toutes les pages publiques</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">*</span>
                  <span>Les utilisateurs peuvent la fermer mais elle r&eacute;appara&icirc;t au prochain chargement</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">*</span>
                  <span>Une seule modale active &agrave; la fois (la plus r&eacute;cente)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">*</span>
                  <span>Id&eacute;al pour : maintenance, incidents techniques, annonces importantes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Incidents History */}
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
              <RefreshCw className="h-6 w-6 text-[#00FFFF]" />
              <span>Historique des incidents</span>
            </h2>
            <button
              onClick={fetchIncidents}
              className="text-[#00FFFF] hover:text-white transition-colors text-sm flex items-center space-x-1"
              data-testid="refresh-incidents-btn"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Rafra&icirc;chir</span>
            </button>
          </div>

          {loadingIncidents ? (
            <div className="text-center py-8 text-[#6B6B6E]">Chargement...</div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-8 text-[#6B6B6E]">
              Aucun incident cr&eacute;&eacute; pour le moment
            </div>
          ) : (
            <div className="space-y-4">
              {incidents.map((incident) => (
                <div
                  key={incident.id}
                  className={`p-4 rounded-lg border flex items-center justify-between ${
                    incident.is_active
                      ? getPriorityColor(incident.priority)
                      : 'border-[#3A3A3D] bg-[#222226] opacity-60'
                  }`}
                  data-testid={`incident-item-${incident.id}`}
                >
                  <div className="flex items-start space-x-3 flex-1">
                    {getPriorityIcon(incident.priority)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-white font-bold truncate">{incident.title}</h4>
                        {incident.is_active ? (
                          <span className="text-xs bg-[#00FF88]/20 text-[#00FF88] px-2 py-0.5 rounded-full">Active</span>
                        ) : (
                          <span className="text-xs bg-[#6B6B6E]/20 text-[#6B6B6E] px-2 py-0.5 rounded-full">D&eacute;sactiv&eacute;e</span>
                        )}
                        {incident.email_sent && (
                          <span className="text-xs bg-[#00FFFF]/20 text-[#00FFFF] px-2 py-0.5 rounded-full">Email envoy&eacute;</span>
                        )}
                      </div>
                      <p className="text-[#B8B8BA] text-sm truncate">{incident.message}</p>
                      <p className="text-[#6B6B6E] text-xs mt-1">{formatDate(incident.created_at)}</p>
                    </div>
                  </div>
                  {incident.is_active && (
                    <button
                      onClick={() => handleDeactivate(incident.id)}
                      className="ml-4 p-2 bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 rounded-lg hover:bg-[#FF3B5C]/20 transition-colors flex-shrink-0"
                      title="D\u00e9sactiver"
                      data-testid={`deactivate-incident-${incident.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-[#FF3B5C]" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentModal;
